import { Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Deal, DealFundingLender, FinderFeeAgreement } from '../../shared/models';
import { DateUtils, EnumUtils } from '../../shared/utils';
import { CerebroSecondaryButton } from '../../shared/components';
import { EmptyGuid } from '../../shared/constants';
import NumberFormat from 'react-number-format';

interface FundingLenderTableProps {
	fundingLenders: DealFundingLender[];
	deal: Deal;
	onAdd: (lender: DealFundingLender) => void;
	onEdit: (lender: DealFundingLender) => void;
}

export const FundingLenderTable: React.FC<FundingLenderTableProps> = ({ fundingLenders, deal, onAdd, onEdit }) => {
	if (!fundingLenders) {
		return null;
	}

	if (fundingLenders.length === 0) {
		return null;
	}

	const totalFundedAmount = fundingLenders.reduce((sum, lender) => sum + lender.amountFunded, 0);
	const showActions = deal.amountFunded != totalFundedAmount;
	const remainingAmount = deal.amountFunded - totalFundedAmount;

	return (
		<Box sx={{ marginTop: 2 }}>
			<TableContainer>
				<Table>
					<TableHead>
						{showActions && (
							<TableRow>
								<TableCell colSpan={6}>
									<CerebroSecondaryButton
										onClick={() =>
											onAdd({
												id: 0,
												guid: EmptyGuid,
												bankContactGuid: EmptyGuid,
												lenderName: '',
												bankName: '',
												amountFunded: remainingAmount,
												finderFeeAgreement: null,
												dateFunded: deal.dateFunded,
											} as DealFundingLender)
										}
									>
										Add Funding Lender
									</CerebroSecondaryButton>
								</TableCell>
							</TableRow>
						)}
						<TableRow>
							<TableCell>Funding Lender</TableCell>
							<TableCell>Bank</TableCell>
							<TableCell>Finder Fee</TableCell>
							<TableCell>Date Funded</TableCell>
							<TableCell>Amount</TableCell>
							{showActions && <TableCell></TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{fundingLenders.map(lender => (
							<TableRow key={lender.id}>
								<TableCell>{lender.lenderName}</TableCell>
								<TableCell>{lender.bankName}</TableCell>
								<TableCell>
									<FinderFeeDisplay finderFee={lender.finderFeeAgreement} />
								</TableCell>
								<TableCell>
									{DateUtils.FormatStringAsShortDate(lender.dateFunded?.toString())}
								</TableCell>
								<TableCell>
									<NumberFormat
										value={lender.amountFunded}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>
								</TableCell>
								{showActions && (
									<TableCell>
										<CerebroSecondaryButton onClick={() => onEdit(lender)}>
											Edit
										</CerebroSecondaryButton>
									</TableCell>
								)}
							</TableRow>
						))}
						{showActions && (
							<TableRow>
								<TableCell colSpan={4} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
									Total Amount
								</TableCell>
								<TableCell>
									<NumberFormat
										value={totalFundedAmount}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>
								</TableCell>
								{showActions && <TableCell></TableCell>}
							</TableRow>
						)}
						<TableRow>
							<TableCell colSpan={4} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
								Total Funding Amount
							</TableCell>
							<TableCell>
								<NumberFormat
									value={deal.amountFunded}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>
							</TableCell>
							{showActions && <TableCell></TableCell>}
						</TableRow>
						{showActions && (
							<TableRow>
								<TableCell colSpan={6}>
									<Alert severity="info">
										The total funding lender amounts need to equal the specified funding amount for
										the deal.
									</Alert>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

interface FinderFeeDisplayProps {
	finderFee: FinderFeeAgreement | null;
}

export const FinderFeeDisplay: React.FC<FinderFeeDisplayProps> = ({ finderFee }) => {
	if (finderFee == null) {
		return null;
	}

	return (
		<span>
			{finderFee.scope} | {EnumUtils.GetTier3ContactTypeDescription(finderFee.tier3ContactType)}
		</span>
	);
};
